import {
  Project,
  ProjectDetail,
  Task,
  Artifact,
  CreateProjectInput,
  UpdateProjectInput,
  CreateTaskInput,
  UpdateTaskInput,
  CreateArtifactInput,
  UpdateArtifactInput,
  UploadArtifactInput,
  ProjectStatus,
} from "../types/dashboard";

const API_BASE_URL = "https://api.serga.ba";

export class ProjectsService {
  private static authTokens: any = null;

  static setAuthTokens(tokens: any) {
    this.authTokens = tokens;
  }

  private static getAuthHeaders() {
    if (!this.authTokens?.idToken) {
      throw new Error("No authentication token available");
    }
    return {
      Authorization: `Bearer ${this.authTokens.idToken}`,
      "Content-Type": "application/json",
    };
  }

  private static async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let message = `Request failed: ${response.status}`;
      try {
        const parsed = JSON.parse(errorBody);
        if (parsed.error) message = parsed.error;
      } catch {}
      throw new Error(message);
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  // ======== Projects ========

  static async listProjects(status?: ProjectStatus): Promise<Project[]> {
    const query = status ? `?status=${status}` : "";
    const data = await this.request<{ projects: Project[] }>(
      `/projects${query}`,
    );
    return data.projects;
  }

  static async getProject(projectId: string): Promise<ProjectDetail> {
    return this.request<ProjectDetail>(`/projects/${projectId}`);
  }

  static async createProject(input: CreateProjectInput): Promise<Project> {
    return this.request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  static async updateProject(
    projectId: string,
    input: UpdateProjectInput,
  ): Promise<Project> {
    return this.request<Project>(`/projects/${projectId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  static async deleteProject(projectId: string): Promise<void> {
    await this.request(`/projects/${projectId}`, { method: "DELETE" });
  }

  // ======== Tasks ========

  static async createTasks(
    projectId: string,
    tasks: CreateTaskInput[],
  ): Promise<Task[]> {
    const data = await this.request<{ tasks: Task[] }>(
      `/projects/${projectId}/tasks`,
      {
        method: "POST",
        body: JSON.stringify({ tasks }),
      },
    );
    return data.tasks;
  }

  static async updateTask(
    projectId: string,
    taskId: string,
    input: UpdateTaskInput,
  ): Promise<Task> {
    return this.request<Task>(`/projects/${projectId}/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  static async deleteTask(projectId: string, taskId: string): Promise<void> {
    await this.request(`/projects/${projectId}/tasks/${taskId}`, {
      method: "DELETE",
    });
  }

  static async reorderTasks(
    projectId: string,
    taskIds: string[],
  ): Promise<Task[]> {
    const data = await this.request<{ tasks: Task[] }>(
      `/projects/${projectId}/tasks/reorder`,
      {
        method: "PUT",
        body: JSON.stringify({ taskIds }),
      },
    );
    return data.tasks;
  }

  // ======== Artifacts ========

  static async createArtifact(
    projectId: string,
    input: CreateArtifactInput,
  ): Promise<Artifact> {
    return this.request<Artifact>(`/projects/${projectId}/artifacts`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  static async updateArtifact(
    projectId: string,
    artifactId: string,
    input: UpdateArtifactInput,
  ): Promise<Artifact> {
    return this.request<Artifact>(
      `/projects/${projectId}/artifacts/${artifactId}`,
      {
        method: "PATCH",
        body: JSON.stringify(input),
      },
    );
  }

  static async uploadArtifact(
    projectId: string,
    input: UploadArtifactInput,
  ): Promise<Artifact & { uploadUrl: string }> {
    return this.request<Artifact & { uploadUrl: string }>(
      `/projects/${projectId}/artifacts/upload`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  }

  static async deleteArtifact(
    projectId: string,
    artifactId: string,
  ): Promise<void> {
    await this.request(`/projects/${projectId}/artifacts/${artifactId}`, {
      method: "DELETE",
    });
  }
}
